/**
 * "My inventions" — the Shape & Strategy view (the figure's fifth tab).
 *
 * The atlas draws the shape; this reads it. Every hole, bridge and frontier
 * direction the topology run validated IN FULL SPACE, with the score the WOS-P
 * run gave it and the inventions whose documents border it.
 *
 *   GET /whitespace/portfolio_topology?scope=own  (which candidates survived)
 *   GET /whitespace/portfolio_score?scope=own     (the same candidates, scored)
 *
 * The two payloads describe the same candidates, so the rows below are the
 * SCORED ones — a region that has no score is listed with its type and its
 * bordering inventions and no invented number.
 */
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import { getPortfolioScore, getPortfolioTopology } from '../../services/api'
import LoadingSkeleton from '../shared/LoadingSkeleton'
import ConfidenceGate from '../shared/ConfidenceGate'
import { OWN_SCOPE_ENTITLEMENT_LINE } from './ownScopeViews'
import { PORTFOLIO_INSUFFICIENT_BASIS_REASON } from '../../lib/whitespace'
import type { Project } from '../../types'
import type { PortfolioScoreRegion } from '../../types/whitespace'

const OWN = 'own' as const

const KIND_LABEL: Record<string, string> = {
  hole: 'Hole — open ground your documents surround but do not occupy',
  bridge: 'Bridge — a thin corridor joining two parts of your set',
  frontier: 'Frontier direction — open ground at the edge of your set',
  frontier_edge: 'Frontier direction — open ground at the edge of your set',
}

const SUB_SCORES: { key: keyof PortfolioScoreRegion; label: string }[] = [
  { key: 'sparsity', label: 'Sparsity' },
  { key: 'adjacency', label: 'Adjacency' },
  { key: 'momentum', label: 'Momentum' },
  { key: 'enablement', label: 'Enablement' },
  { key: 'enclosure', label: 'Enclosure' },
  { key: 'synergy', label: 'Synergy' },
]

export interface OwnShapeStrategyViewProps {
  owned: Project[]
}

export default function OwnShapeStrategyView({ owned }: OwnShapeStrategyViewProps) {
  const topologyQuery = useQuery({
    queryKey: ['portfolio-topology', 'own'],
    queryFn: () => getPortfolioTopology(null, OWN),
    retry: false,
  })
  const scoreQuery = useQuery({
    queryKey: ['portfolio-score', 'own'],
    queryFn: () => getPortfolioScore(null, OWN),
    retry: false,
  })

  const names = useMemo(() => new Map(owned.map((p) => [p.project_id, p.name])), [owned])
  const regions = useMemo(
    () => [...(scoreQuery.data?.regions ?? [])].sort((a, b) => b.wos_p_score - a.wos_p_score),
    [scoreQuery.data],
  )
  const topology = topologyQuery.data

  if (topologyQuery.isLoading || scoreQuery.isLoading) return <LoadingSkeleton type="card" rows={4} />

  if (topologyQuery.isError && scoreQuery.isError) {
    return (
      <p role="alert" className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-[12.5px] text-amber-900">
        We could not load your topology run, so there is no shape to read yet.
      </p>
    )
  }

  return (
    <div data-part="OwnShapeStrategyView" className="space-y-3">
      <div className="flex items-start gap-2.5 rounded-lg border border-iflow-200 bg-iflow-50/70 px-3 py-2.5 text-[12px] leading-relaxed text-iflow-900">
        <span aria-hidden className="mt-0.5 font-bold text-iflow-600">ℹ</span>
        <p>
          <b>The shape of your set, in words.</b> Holes, bridges and frontier directions are
          validated in the full embedding space, not read off the flat picture — a shape that only
          exists in two dimensions is discarded before it reaches this list.
        </p>
      </div>

      <p className="text-[12px] text-gray-600">
        {topology
          ? `${topology.holes.length} hole${topology.holes.length === 1 ? '' : 's'}, ${topology.bridges.length} bridge${topology.bridges.length === 1 ? '' : 's'} and ${topology.frontier_edges.length} frontier direction${topology.frontier_edges.length === 1 ? '' : 's'} survived validation, out of ${topology.candidate_count} candidate${topology.candidate_count === 1 ? '' : 's'} (${topology.discarded_candidate_count} discarded as a 2-D artifact)`
          : 'The topology run did not come back, so the counts above the list are unavailable'}{' '}
        · <b className="font-semibold text-iflow-800">{OWN_SCOPE_ENTITLEMENT_LINE}</b>
      </p>

      {regions.length === 0 ? (
        <p className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-[12.5px] text-gray-600">
          No region survived validation in full space, so there is nothing to rank. This is a
          measured result, not a missing one — the run completed and found none.
        </p>
      ) : (
        <ul className="space-y-2.5">
          {regions.map((r, i) => (
            <li key={`${r.type}-${i}`} className="rounded-xl border border-gray-200 bg-white p-3.5">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[13px] font-bold text-gray-900">
                    {KIND_LABEL[r.type] ?? r.type}
                  </p>
                  <p className="mt-0.5 text-[11.5px] text-gray-500">
                    Bordered by{' '}
                    {r.member_project_ids.length === 0
                      ? 'no invention the roster can name'
                      : r.member_project_ids.map((id) => names.get(id) ?? id).join(', ')}
                  </p>
                </div>
                <span className="shrink-0 rounded-full bg-iflow-100 px-2.5 py-1 text-[12px] font-bold text-iflow-800">
                  {r.wos_p_score}
                </span>
              </div>
              <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500">
                {SUB_SCORES.map((s) => (
                  <div key={s.key} className="flex gap-1">
                    <dt>{s.label}</dt>
                    <dd className="font-semibold text-gray-700">{String(r[s.key])}</dd>
                  </div>
                ))}
                <div className="flex gap-1">
                  <dt>Documents on its boundary</dt>
                  <dd className="font-semibold text-gray-700">{r.boundary_record_ids.length}</dd>
                </div>
              </dl>
              {r.insufficient_basis && (
                <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50/60">
                  <ConfidenceGate
                    icon={<AlertTriangle />}
                    title="Sub-scores are provisional"
                    description={PORTFOLIO_INSUFFICIENT_BASIS_REASON}
                    compact
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
